import Head from "next/head";
import CalculatorQuoteRequest from "@/components/tools/CalculatorQuoteRequest";
import React, { useState, useCallback } from "react";
import { ENV } from "@/core/config/env";
import dynamic from 'next/dynamic';
import { usePopup } from '@/contexts/PopupContext';
import { Calculator, ShoppingCart, Zap, Settings } from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
// Add the dynamic import
const RequestItemFormPopup = dynamic(() => import("@/components/requestItem/RequestItemFormPopup"), {
  ssr: false,
});


interface TurboCalculationResult {
  recommendedTurboSize: string;
  cfmRequired: number;
  pressureRatio: number;
  turbineAR: number;
  compressorAR: number;
  recommendations: string[];
  warnings: string[];
}

export default function TurboSizeCalculator() {
  const [engineDisplacement, setEngineDisplacement] = useState<string>("");
  const [targetHorsepower, setTargetHorsepower] = useState<string>("");
  const [currentHorsepower, setCurrentHorsepower] = useState<string>("");
  const [redlineRPM, setRedlineRPM] = useState<string>("");
  const [targetBoostPressure, setTargetBoostPressure] = useState<string>("");
  const [engineType, setEngineType] = useState<string>("4-stroke");
  const [fuelType, setFuelType] = useState<string>("petrol");
  const [aspirationType, setAspirationRe] = useState<string>("naturally-aspirated");
  const [result, setResult] = useState<TurboCalculationResult | null>(null);

   // Add popup context
  const { 
    isPopupOpen, 
    popupType, 
    resetPopup,
    setPopupOpen,
    setPopupType
  } = usePopup();

  // Add local state as backup
  const [localShowPopup, setLocalShowPopup] = useState(false);

  // Add popup handling functions
  const handleTurboQuoteRequest = (quoteType: string, turboData?: any) => {
    try {
      console.log('=== TURBO QUOTE REQUEST CLICKED ===');
      console.log('Quote Type:', quoteType);
      
      // Include current turbo calculations and engine specifications
      const turboInfo = {
        engineSpecifications: {
          displacement: engineDisplacement,
          currentHP: currentHorsepower,
          targetHP: targetHorsepower,
          redlineRPM: redlineRPM,
          targetBoost: targetBoostPressure,
          engineType: engineType,
          fuelType: fuelType,
          aspirationType: aspirationType
        },
        calculatedResults: result ? {
          recommendedTurbo: result.recommendedTurboSize,
          cfmRequired: result.cfmRequired,
          pressureRatio: result.pressureRatio,
          turbineAR: result.turbineAR,
          compressorAR: result.compressorAR,
          recommendations: result.recommendations,
          warnings: result.warnings
        } : null,
        ...turboData
      };

      // Set initial data for the turbo quote request
      const serviceData = {
        title: result 
          ? `Turbo Quotes for ${result.recommendedTurboSize} - ${engineDisplacement}L Engine`
          : `Turbocharger Quote Request - ${engineDisplacement || 'Custom'} Engine`,
        description: result 
          ? `Get competitive quotes for ${result.recommendedTurboSize} turbocharger. Target: ${targetHorsepower}HP, CFM Required: ${result.cfmRequired}, Pressure Ratio: ${result.pressureRatio}:1. Engine: ${engineDisplacement}L ${engineType} ${fuelType}.`
          : `Get professional turbocharger quotes and consultation services. Engine specifications: ${engineDisplacement || 'TBD'}L displacement, targeting ${targetHorsepower || 'custom'}HP output.`,
        serviceId: 'turbo-quotes',
        categoryId: 'performance-parts',
        subCategoryId: 'turbochargers',
        domainCategoryId: 'automotive',
        turboCalculatorData: turboInfo
      };

      // Store in sessionStorage
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('selectedServiceData', JSON.stringify(serviceData));
      }

      // Open popup using available methods
      if (typeof setPopupOpen === 'function' && typeof setPopupType === 'function') {
        console.log('Using context methods: setPopupType + setPopupOpen');
        setPopupType('carousel');
        setPopupOpen(true);
      } else {
        console.log('Using local state fallback');
        setLocalShowPopup(true);
      }
      
    } catch (error) {
      console.error('Error in handleTurboQuoteRequest:', error);
    }
  };

  // Add popup close handler
  const handleClosePopup = () => {
    try {
      console.log('=== CLOSING POPUP ===');
      
      if (typeof resetPopup === 'function') {
        resetPopup();
      }
      
      if (typeof setPopupOpen === 'function') {
        setPopupOpen(false);
      }
      
      setLocalShowPopup(false);
      
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('selectedServiceData');
      }
    } catch (error) {
      console.error('Error in handleClosePopup:', error);
    }
  };

  // Add getInitialData function
  const getInitialData = useCallback(() => {
    if (typeof window !== 'undefined') {
      const storedData = sessionStorage.getItem('selectedServiceData');
      if (storedData) {
        try {
          return JSON.parse(storedData);
        } catch (e) {
          console.error('Error parsing stored service data:', e);
        }
      }
    }
    return undefined;
  }, []);

  const calculateTurboSize = () => {
    const displacement = parseFloat(engineDisplacement);
    const targetHP = parseFloat(targetHorsepower);
    const currentHP = parseFloat(currentHorsepower);
    const rpm = parseFloat(redlineRPM);
    const boostPSI = parseFloat(targetBoostPressure);

    if (!displacement || !targetHP || !rpm) {
      alert("Please fill in all required fields");
      return;
    }

    // Calculate CFM required
    const volumetricEfficiency = 0.85; // Typical VE for turbocharged engines
    const cfmRequired = (displacement * rpm * volumetricEfficiency * (1 + (boostPSI || 0) / 14.7)) / 3456;

    // Calculate pressure ratio
    const pressureRatio = 1 + (boostPSI || 0) / 14.7;

    // Determine turbo size based on CFM and pressure ratio
    let recommendedTurboSize = "";
    let turbineAR = 0;
    let compressorAR = 0;

    if (cfmRequired <= 300) {
      recommendedTurboSize = "GT2554R / GT2560R";
      turbineAR = 0.64;
      compressorAR = 0.60;
    } else if (cfmRequired <= 400) {
      recommendedTurboSize = "GT2860RS / GT2871R";
      turbineAR = 0.73;
      compressorAR = 0.64;
    } else if (cfmRequired <= 500) {
      recommendedTurboSize = "GT3071R / GT3076R";
      turbineAR = 0.82;
      compressorAR = 0.70;
    } else if (cfmRequired <= 650) {
      recommendedTurboSize = "GT3582R / GT35R";
      turbineAR = 1.06;
      compressorAR = 0.82;
    } else {
      recommendedTurboSize = "GT4088R / GT4094R";
      turbineAR = 1.15;
      compressorAR = 1.00;
    }

    // Generate recommendations
    const recommendations = [];
    const warnings = [];

    if (pressureRatio > 3.0) {
      warnings.push("High pressure ratio may require intercooling and engine internals upgrade");
    }

    if (targetHP > currentHP * 2) {
      warnings.push("Doubling horsepower requires significant supporting modifications");
      recommendations.push("Consider upgrading fuel system, internals, and ECU tuning");
    }

    if (cfmRequired > 500) {
      recommendations.push("Large turbo may have increased lag - consider twin-scroll design");
    }

    recommendations.push(`Recommended A/R ratio: Turbine ${turbineAR}, Compressor ${compressorAR}`);
    recommendations.push("Professional tuning is essential for optimal performance");

    setResult({
      recommendedTurboSize,
      cfmRequired: Math.round(cfmRequired),
      pressureRatio: Math.round(pressureRatio * 100) / 100,
      turbineAR,
      compressorAR,
      recommendations,
      warnings
    });
  };

  const resetCalculator = () => {
    setEngineDisplacement("");
    setTargetHorsepower("");
    setCurrentHorsepower("");
    setRedlineRPM("");
    setTargetBoostPressure("");
    setEngineType("4-stroke");
    setFuelType("petrol");
    setAspirationRe("naturally-aspirated");
    setResult(null);
  };

  const title = "Turbo Size Calculator | Online Automotive Tools | Car Spares EzyFind";
  const description = "Calculate the optimal turbocharger size for your engine. Professional turbo sizing tool based on engine displacement, target horsepower, and boost pressure requirements.";
  const keywords = "turbo size calculator, turbocharger sizing, engine turbo calculator, boost calculator, automotive tools, car modification tools";
  const canonicalUrl = `${ENV.DOMAIN_URL}/online-automotive-calculators-and-tools/turbo-size-calculator`;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Head>

  <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* UPDATED Header with Request Turbo Quotes Button */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">Turbo Size Calculator</h1>
              <p className="text-lg text-gray-600 mb-6">
                Calculate the optimal turbocharger size for your engine based on displacement, target horsepower, and boost requirements.
              </p>
              
              {/* NEW REQUEST TURBO QUOTES BUTTON */}
              <div className="mb-6">
                <button
                  onClick={() => handleTurboQuoteRequest('Turbo Calculator Quote Request')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-3 mx-auto text-lg"
                >
                  <Zap className="w-6 h-6" />
                  Request for turbo quotes
                </button>
                <p className="text-gray-500 mt-2 text-sm">
                  Get competitive quotes from verified turbo suppliers
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Calculator Form */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">Engine Specifications</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Engine Displacement (Liters) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={engineDisplacement}
                      onChange={(e) => setEngineDisplacement(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 2.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Horsepower
                    </label>
                    <input
                      type="number"
                      value={currentHorsepower}
                      onChange={(e) => setCurrentHorsepower(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 150"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Horsepower *
                    </label>
                    <input
                      type="number"
                      value={targetHorsepower}
                      onChange={(e) => setTargetHorsepower(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redline RPM *
                    </label>
                    <input
                      type="number"
                      value={redlineRPM}
                      onChange={(e) => setRedlineRPM(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 7000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Boost Pressure (PSI)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={targetBoostPressure}
                      onChange={(e) => setTargetBoostPressure(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 15"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Engine Type
                    </label>
                    <select
                      value={engineType}
                      onChange={(e) => setEngineType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Select engine type"
                    >
                      <option value="4-stroke">4-Stroke</option>
                      <option value="2-stroke">2-Stroke</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuel Type
                    </label>
                    <select
                      value={fuelType}
                      onChange={(e) => setFuelType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Select fuel type"
                    >
                      <option value="petrol">Petrol</option>
                      <option value="diesel">Diesel</option>
                      <option value="e85">E85</option>
                    </select>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={calculateTurboSize}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200"
                    >
                      Calculate Turbo Size
                    </button>
                    <button
                      onClick={resetCalculator}
                      className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition duration-200"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">Calculation Results</h2>
                
                {result ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg text-blue-800 mb-2">Recommended Turbo</h3>
                      <p className="text-blue-700 text-xl font-bold">{result.recommendedTurboSize}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">CFM Required</p>
                        <p className="text-lg font-semibold">{result.cfmRequired}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Pressure Ratio</p>
                        <p className="text-lg font-semibold">{result.pressureRatio}:1</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Turbine A/R</p>
                        <p className="text-lg font-semibold">{result.turbineAR}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600">Compressor A/R</p>
                        <p className="text-lg font-semibold">{result.compressorAR}</p>
                      </div>
                    </div>
                 <div className="text-center">
                      <button
                        onClick={() => handleTurboQuoteRequest(`${result.recommendedTurboSize} Turbo Quote`, {
                          calculatedTurbo: true,
                          turboModel: result.recommendedTurboSize,
                          cfmRequired: result.cfmRequired,
                          pressureRatio: result.pressureRatio
                        })}
                        className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        Get Quotes for {result.recommendedTurboSize}
                      </button>
                    </div>
                    {result.warnings.length > 0 && (
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-red-800 mb-2">⚠️ Warnings</h3>
                        <ul className="text-red-700 space-y-1">
                          {result.warnings.map((warning, index) => (
                            <li key={index} className="text-sm">• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-green-800 mb-2">💡 Recommendations</h3>
                      <ul className="text-green-700 space-y-1">
                        {result.recommendations.map((rec, index) => (
                          <li key={index} className="text-sm">• {rec}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                <div className="text-center text-gray-500 py-8">
                    <Calculator size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="mb-6">Enter your engine specifications and click "Calculate Turbo Size" to see results.</p>
                    {/* QUOTE BUTTON WHEN NO CALCULATION */}
                    <button
                      onClick={() => handleTurboQuoteRequest('General Turbo Consultation')}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Settings className="w-5 h-5" />
                      Request Turbo Consultation
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Information Section */}
            <CalculatorQuoteRequest
              toolId="turbo-size-calculator"
              title="Request multiple turbo quotes"
              description="Request multiple quotes from different turbo providers. Include your vehicle and engine details, calculator results and performance goals, choose your location, and attach photos or part numbers in the upload step."
            />

            <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">How Turbo Sizing Works</h2>
              <div className="prose max-w-none text-gray-700">
                <p className="mb-4">
                  Turbocharger sizing is critical for optimal engine performance. Our calculator considers several key factors:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Engine Displacement:</strong> Determines base airflow requirements</li>
                  <li><strong>Target Horsepower:</strong> Helps calculate required boost and airflow</li>
                  <li><strong>RPM Range:</strong> Affects turbo sizing for response vs. peak power</li>
                  <li><strong>Pressure Ratio:</strong> Determines compressor efficiency range</li>
                </ul>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-800">
                    <strong>Disclaimer:</strong> This calculator provides estimates based on general formulas. 
                    Professional consultation and dyno tuning are recommended for optimal results.
                  </p>
                </div>
              </div>
            </div>
           {/* Understanding Turbochargers */}
<div className="mt-8 bg-white rounded-lg shadow-lg p-6">
  <h2 className="text-2xl font-semibold mb-6 text-gray-800">Understanding Turbochargers</h2>
  
  <div className="space-y-8">
    
    {/* What is a Turbo */}
    <div>
      <h3 className="text-xl font-semibold mb-4 text-blue-600">What is a Turbocharger?</h3>
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          A turbocharger, commonly called a "turbo," acts as a power amplifier for your vehicle's engine. 
          Think of it as an advanced breathing system that helps your engine inhale more efficiently.
        </p>
        <p className="mb-4">
          While naturally aspirated (NA) engines draw air directly from the atmosphere, turbochargers 
          utilize exhaust gases to spin a turbine wheel. This turbine connects to a compressor wheel, 
          which compresses and forces additional air into the engine. The result? More air allows for 
          more fuel combustion, creating a substantial power increase.
        </p>
        <p className="mb-4">
          Unlike superchargers that rely on belt-driven mechanical power, turbochargers harness the 
          engine's waste exhaust gases, making them more efficient at boosting power without drawing 
          additional energy from the engine itself.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
          <p className="text-blue-800 font-semibold">
            💡 Simply put: A turbocharger transforms your engine's waste exhaust into extra power!
          </p>
        </div>
      </div>
    </div>

    {/* Turbo Construction */}
    <div>
      <h3 className="text-xl font-semibold mb-4 text-green-600">Turbocharger Construction & Sizing</h3>
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          Turbochargers consist of two primary sections: the turbine section and the compressor section. 
          Our calculator determines turbo sizes based on the compressor's inducer diameter, which is the 
          industry standard method for turbocharger classification.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 my-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-bold text-gray-800 mb-2">Turbine Section</h4>
            <p className="text-sm text-gray-700">
              Captures exhaust gas energy and converts it into rotational motion. Features both 
              inducer and exducer measurements.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-bold text-gray-800 mb-2">Compressor Section</h4>
            <p className="text-sm text-gray-700">
              Compresses incoming air and forces it into the engine. The inducer diameter 
              determines the turbo's size designation.
            </p>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 italic">
          When discussing turbo sizes, measurements like "48mm" refer to the compressor's inducer 
          diameter measured in millimeters.
        </p>
      </div>
    </div>

    {/* Can You Turbocharge Any Engine */}
    <div>
      <h3 className="text-xl font-semibold mb-4 text-purple-600">Can Any Engine Be Turbocharged?</h3>
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          Turbocharging is an effective method to increase engine power, and our calculator specifically 
          focuses on petrol, naturally aspirated engines. However, adding a turbocharger involves more 
          complexity than simply bolting on the unit.
        </p>
        <p className="mb-4">
          Many engines, particularly older naturally aspirated petrol engines, can accommodate turbocharging 
          with proper modifications. While diesel engines commonly feature turbochargers as standard equipment, 
          many petrol vehicles remain naturally aspirated without any forced induction systems.
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-6">
          <h4 className="font-bold text-yellow-800 mb-3">Essential Requirements for Turbocharging:</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-semibold text-yellow-700 mb-2">Engine Internals</h5>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Reinforced pistons and connecting rods</li>
                <li>• Upgraded crankshaft (for high boost applications)</li>
                <li>• Strengthened engine block and head</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-yellow-700 mb-2">Supporting Systems</h5>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Enhanced cooling system capacity</li>
                <li>• Upgraded fuel delivery system</li>
                <li>• Sufficient engine bay clearance</li>
                <li>• ECU tuning and management</li>
              </ul>
            </div>
          </div>
        </div>

        <p className="mb-4">
          The power increase from turbocharging varies significantly based on boost pressure, engine 
          condition, and supporting modifications. Additional considerations may include upgrading 
          tires for improved traction and enhancing the exhaust system for optimal flow.
        </p>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">
            <strong>⚠️ Important:</strong> Our calculator is designed specifically for naturally aspirated, 
            petrol-powered engines. Always consult with automotive professionals before undertaking 
            turbocharging projects.
          </p>
        </div>
      </div>
    </div>

    {/* How to Calculate Turbo Size */}
    <div>
      <h3 className="text-xl font-semibold mb-4 text-red-600">Calculating Optimal Turbo Size</h3>
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          Determining the correct turbocharger size requires careful consideration of multiple factors. 
          Our calculator simplifies this complex process using industry-proven methodologies, particularly 
          following guidelines established by leading manufacturers like Garrett Motion.
        </p>
        <p className="mb-4">
          The calculation primarily focuses on two critical parameters: your engine's displacement and 
          your target horsepower output. This approach allows for accurate turbo selection based on 
          proven engineering principles and real-world performance data.
        </p>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 my-6">
          <h4 className="font-bold text-green-800 mb-3">Key Calculation Factors:</h4>
          <div className="space-y-3">
            <div className="flex items-start">
              <span className="text-green-600 mr-3 mt-1">📏</span>
              <div>
                <h5 className="font-semibold text-green-700">Engine Displacement</h5>
                <p className="text-green-600 text-sm">Determines base airflow requirements and turbo capacity needs</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 mr-3 mt-1">🔥</span>
              <div>
                <h5 className="font-semibold text-green-700">Target Power Output</h5>
                <p className="text-green-600 text-sm">Influences required boost pressure and compressor sizing</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 mr-3 mt-1">⚡</span>
              <div>
                <h5 className="font-semibold text-green-700">RPM Operating Range</h5>
                <p className="text-green-600 text-sm">Affects turbo response characteristics and efficiency</p>
              </div>
            </div>
          </div>
        </div>

        <p className="mb-4">
          Manual turbo sizing requires extensive research of individual specifications, compressor maps, 
          and performance data. Our calculator eliminates this complexity by providing instant, accurate 
          recommendations based on your specific requirements.
        </p>
      </div>
    </div>

    {/* How to Use the Calculator */}
    <div>
      <h3 className="text-xl font-semibold mb-4 text-indigo-600">Using Our Turbo Size Calculator</h3>
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          Our user-friendly calculator requires minimal input for maximum accuracy. Simply provide 
          the essential parameters to receive comprehensive turbo sizing recommendations.
        </p>

        <div className="grid md:grid-cols-3 gap-4 my-6">
          <div className="bg-indigo-50 p-4 rounded-lg text-center">
            <div className="text-2xl mb-2">🔧</div>
            <h4 className="font-bold text-indigo-800 mb-2">Step 1: Engine Specs</h4>
            <p className="text-indigo-700 text-sm">
              Enter your engine displacement in liters or cubic centimeters
            </p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg text-center">
            <div className="text-2xl mb-2">🎯</div>
            <h4 className="font-bold text-indigo-800 mb-2">Step 2: Power Goals</h4>
            <p className="text-indigo-700 text-sm">
              Input your desired horsepower or kilowatt target
            </p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-lg text-center">
            <div className="text-2xl mb-2">📊</div>
            <h4 className="font-bold text-indigo-800 mb-2">Step 3: Get Results</h4>
            <p className="text-indigo-700 text-sm">
              Receive specific turbo recommendations and sizing data
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
          <h4 className="font-bold text-blue-800 mb-2">Example Calculation:</h4>
          <p className="text-blue-700 text-sm">
            A 1999 BMW 3 Series with a 2.5L (2494cc) naturally aspirated engine targeting 500 horsepower 
            would require approximately a 48mm turbocharger based on compressor inducer diameter.
          </p>
        </div>

        <p className="text-sm text-gray-600">
          The calculator automatically validates your inputs - suggesting that turbocharging isn't necessary 
          for very low power goals, or indicating when targets exceed practical limitations.
        </p>
      </div>
    </div>

  </div>
</div>

{/* FAQ Section */}
<div className="mt-8 bg-white rounded-lg shadow-lg p-6">
  <h2 className="text-2xl font-semibold mb-6 text-gray-800">Frequently Asked Questions</h2>
  
  <div className="space-y-6">
    <div>
      <h3 className="font-bold text-lg mb-2 text-gray-800">What size turbocharger do I need?</h3>
      <p className="text-gray-700 mb-3">
        Choosing the correct turbo size depends on several interconnected factors:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700">
        <li><strong>Engine displacement:</strong> Larger engines typically require bigger turbos for optimal performance</li>
        <li><strong>Supporting modifications:</strong> Engine internals must be prepared for increased boost pressure</li>
        <li><strong>Power objectives:</strong> Higher horsepower targets necessitate larger turbocharger units</li>
        <li><strong>Vehicle application:</strong> Racing applications usually require larger turbos than daily drivers</li>
      </ul>
    </div>
    
    <div>
      <h3 className="font-bold text-lg mb-2 text-gray-800">How do I calculate turbo size manually?</h3>
      <p className="text-gray-700 mb-3">Manual turbo sizing follows these steps:</p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700">
        <li><strong>Determine engine capacity:</strong> Measure displacement (e.g., 2700cc)</li>
        <li><strong>Set power targets:</strong> Define desired horsepower output (e.g., 400hp)</li>
        <li><strong>Research turbo specifications:</strong> Compare available options for your requirements</li>
        <li><strong>Validate selection:</strong> Ensure chosen turbo matches engine size and power goals</li>
      </ol>
      <p className="text-gray-700 mt-2">
        For a 2700cc engine targeting 400 horsepower, you'd typically need approximately a 48mm turbocharger.
      </p>
    </div>
    
    <div>
      <h3 className="font-bold text-lg mb-2 text-gray-800">What size is a stock 5.9 Cummins turbocharger?</h3>
      <p className="text-gray-700">
        The factory turbocharger for a 5.9L Cummins diesel engine, commonly found in Dodge Ram trucks, 
        typically features a Holset HY35W or HX35W unit. These models have a compressor wheel inducer 
        diameter of approximately 56mm. Specific sizing may vary depending on the vehicle's model year 
        and configuration.
      </p>
    </div>
    
    <div>
      <h3 className="font-bold text-lg mb-2 text-gray-800">Can I use both turbocharging and supercharging simultaneously?</h3>
      <p className="text-gray-700">
        No, combining turbocharging and supercharging on the same engine is not recommended or practical. 
        Each forced induction method operates differently - turbochargers utilize exhaust gas energy while 
        superchargers are mechanically driven. Attempting to combine both systems would create mechanical 
        conflicts, excessive complexity, and potential inefficiencies without meaningful performance benefits.
      </p>
    </div>

    <div>
      <h3 className="font-bold text-lg mb-2 text-gray-800">How much horsepower does a turbocharger add?</h3>
      <p className="text-gray-700">
        The horsepower increase from turbocharging varies significantly based on boost pressure, engine 
        condition, and supporting modifications. Generally, turbocharging can increase power output by 
        30-100% or more, depending on the application. Our calculator provides specific estimates based 
        on your engine specifications and target power goals.
      </p>
    </div>
  </div>
</div>           

          </div>
        </div>
      </div>
      <DownloadApp />
       {(
        (isPopupOpen && popupType === 'carousel') || 
        localShowPopup
      ) && (
        <RequestItemFormPopup 
          currentPopupType={popupType || 'carousel'}
          isContextOpen={isPopupOpen || localShowPopup}
          onClose={handleClosePopup}
          initialData={getInitialData()}
        />
      )}
    </>
  );
}